// Case type/status/phase/stage option lists - shared between App.js (which
// threads them into CaseManagement.js) and any other component that needs
// to render/edit these fields (e.g. CaseEditModal.js) without duplicating
// this table.

export const CASE_TYPE_OPTIONS = [
  { value: 'Contract', label: 'Contract Law' },
  { value: 'Criminal', label: 'Criminal Law' },
  { value: 'Civil', label: 'Civil Law' },
  { value: 'Immigration', label: 'Immigration Law' },
  { value: 'Family', label: 'Family Law' },
  { value: 'Property', label: 'Property Law' },
  { value: 'Intellectual_Property', label: 'Intellectual Property' },
  { value: 'Corporate', label: 'Corporate Law' },
  { value: 'Litigation', label: 'Litigation' },
  { value: 'Real_Estate', label: 'Real Estate Law' },
  { value: 'Transactional', label: 'Transactional' },
  { value: 'Advisory', label: 'Advisory' },
  { value: 'Arbitration', label: 'Arbitration' },
  { value: 'Mediation', label: 'Mediation' },
  { value: 'Conveyancing', label: 'Conveyancing' },
  // [WF-G4] A real, actively-created backend case_type (CaseViewSet's debt
  // recovery case auto-creation, views.py) had no matching option here —
  // the Case Type select rendered bound-but-blank when viewing/editing one
  // of these cases, and there was no way to set it manually either.
  { value: 'Debt Recovery', label: 'Debt Recovery' },
  { value: 'Other', label: 'Other' },
];

export const CASE_STATUS_OPTIONS = [
  { value: 'Open', label: 'Open' },
  { value: 'Active', label: 'Active' },
  { value: 'Pending', label: 'Pending' },
  { value: 'On_Hold', label: 'On Hold' },
  { value: 'Closed', label: 'Closed' },
  { value: 'Archived', label: 'Archived' },
];

export const CASE_PHASE_OPTIONS = [
  { value: 'Initiation', label: 'Initiation' },
  { value: 'Preparation', label: 'Preparation' },
  { value: 'Active_Work', label: 'Active Work' },
  { value: 'Resolution', label: 'Resolution' },
  { value: 'Closure', label: 'Closure' },
];

// Dynamic case stage mapping: stages differ per case_type
export const CASE_TYPE_STAGES_MAP = {
  Contract: [
    'Instruction & Scope', 'Due Diligence/Research', 'Drafting', 'Internal Review',
    'Negotiation', 'Finalization', 'Execution (Signing)', 'Vesting/Performance', 'Close-out',
  ],
  Criminal: [
    'Arrest & Charging', 'Initial Appearance/Remand', 'Bail Application', 'Committal Proceedings',
    'Arraignment & Plea', 'Discovery (State Disclosure)', 'Trial/Main Trial', 'Verdict', 'Sentencing', 'Post-Trial',
  ],
  Civil: [
    'Pre-Litigation/Letter of Demand', 'Issue of Summons', 'Service of Process', 'Appearance to Defend',
    'Pleadings Phase', 'Discovery', 'Case Management Conference', 'Pre-Trial Conference', 'Trial', 'Judgment & Order',
  ],
  Immigration: [
    'Intake', 'Localization/Advertising', 'Application Preparation', 'Submission',
    'Review/Interview', 'Notification of Outcome', 'Payment & Stamping', 'Appeals',
  ],
  Family: [
    'Instruction & Marriage Verification', 'Filing of Summons', 'Service', 'Settlement Negotiation',
    'Trial/Hearing', 'Decree Nisi', 'Waiting Period', 'Decree Absolute',
  ],
  Property: [
    'Claim Verification', 'Site Inspection/Survey', 'Notice of Breach/Dispute', 'Mediation',
    'Litigation (Summons)', 'Interdict/Injunction', 'Trial & Judgment',
  ],
  Intellectual_Property: [
    'Search & Clearance', 'Application Filing', 'Formal Examination', 'Publication',
    'Opposition Phase', 'Registration', 'Certification', 'Maintenance',
  ],
  Corporate: [
    'Name Reservation', 'Incorporation', 'Post-Incorporation', 'Annual Compliance',
    'Corporate Actions', 'Record Keeping', 'Dissolution/Liquidation',
  ],
  Litigation: [
    'Instruction & Merit Assessment', 'Pleadings', 'Interlocutory Applications', 'Case Management',
    'Trial Preparation', 'Trial', 'Judgment', 'Bill of Costs/Taxation', 'Appeals',
  ],
  Real_Estate: [
    'Listing/Instruction', 'Offer to Purchase (OTP)', 'Due Diligence', 'Financing',
    'Conditions Precedent', 'Handover to Conveyancer',
  ],
  Transactional: [
    'NDA/Confidentiality', 'Letter of Intent (LOI)', 'Data Room/Due Diligence',
    'Transaction Documents', 'Regulatory Approval', 'Signing & Closing', 'Post-Closing Integration',
  ],
  Advisory: [
    'Query/Request for Opinion', 'Conflict Check', 'Legal Research', 'Drafting Opinion',
    'Senior Review', 'Delivery',
  ],
  Arbitration: [
    'Notice of Arbitration', 'Appointment of Arbitrator', 'Preliminary Meeting',
    'Statement of Claim & Defence', 'Discovery/Evidence Exchange', 'Hearing', 'Award', 'Recognition & Enforcement',
  ],
  Mediation: [
    'Mediation Agreement', 'Mediator Appointment', 'Opening Session', 'Joint & Caucus Sessions',
    'Bargaining & Option Generation', 'Settlement Agreement', 'Termination',
  ],
  Conveyancing: [
    'FICA/KYC & Instruction', 'Document Drafting', 'Rates & Tax Clearances',
    'Compliance Certificate', 'Lost Deed',
    'Bank Guarantee/Payment', 'Lodgement', 'Examination', 'Registration/Execution', 'Delivery',
  ],
  Other: [
    'Fact Finding & Strategy', 'Action & Drafting', 'Negotiation & Resolution',
    'Court/Formal Proceedings', 'Post-Resolution/Closure',
  ],
};

/** Stage options for a given case_type -> [{value, label}]. */
export function getStagesForType(caseType) {
  const stages = CASE_TYPE_STAGES_MAP[caseType] || CASE_TYPE_STAGES_MAP['Other'];
  return stages.map(s => ({ value: s, label: s }));
}

/** Flat, deduped, sorted list of every stage across all types (for filter dropdowns). */
export const CASE_STAGE_OPTIONS = Array.from(
  new Set(Object.values(CASE_TYPE_STAGES_MAP).flat())
).sort().map(s => ({ value: s, label: s }));
